/**
 * One place that decides what clicking a `doppler://` citation does.
 *
 * There are now two in-app viewers — the workbook modal and the PDF modal — and
 * a new-tab fallback for everything else. Every surface that renders a citation
 * (the chat's markdown anchors, the evidence cards) has to make the same choice,
 * so the choice lives here rather than being re-implemented per caller: a new
 * viewer is wired in once, and a surface rendered outside the providers still
 * degrades to the signed URL instead of dead-clicking.
 */

import { useCallback } from "react";

import { usePdfViewer } from "../context/PdfViewerContext";
import { useSpreadsheetViewer } from "../context/SpreadsheetViewerContext";
import { openCitationTab, openDopplerUri } from "../utils/dopplerUri";

export interface DopplerOpenOpts {
	/** Required when the URI references a chat-scoped workbook artifact. */
	chatId?: string | null;
	/** Display name for the viewer header, when the caller knows it. */
	filename?: string;
}

export type DopplerUriOpener = (href: string, opts?: DopplerOpenOpts) => void;

export const useDopplerUriOpener = (): DopplerUriOpener => {
	const spreadsheetViewer = useSpreadsheetViewer();
	const pdfViewer = usePdfViewer();

	return useCallback(
		(href: string, opts?: DopplerOpenOpts) => {
			if (!href) return;
			// With the tab flag on, a cited file opens on its own page in a
			// separate tab; the modals still take what has no file page
			// (chat-scoped workbook artifacts).
			const opened =
				openCitationTab(href) ||
				(spreadsheetViewer?.openSpreadsheetHref(href, opts) ?? false) ||
				(pdfViewer?.openPdfHref(href, opts) ?? false);
			// `doc` URIs, and any kind whose viewer isn't mounted, keep the
			// pre-viewer behaviour: resolve a signed URL and open a new tab.
			if (!opened) void openDopplerUri(href, { chatId: opts?.chatId });
		},
		[spreadsheetViewer, pdfViewer],
	);
};
